import { type ReactNode, useState } from "react";
import { useTranslations } from "@acto/web-common/locales";
import { ManagementSidepanel } from "../../Sidepanel/ManagementSidepanel";
import type { CustomerMetric } from "../CustomerDetailsSidePanel/customerMetric";
import { type DateRange, PeriodOption } from "../../DateRangeSelector/types";
import { LevelSwitcher } from "./ui/LevelSwitcher";
import { ProductDrillLevel } from "./ui/ProductDrillLevel";
import type { ProductDrillRoot, ProductGroupRef } from "./productDrill";

interface ProductDrilldownSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  isShrunk: boolean;
  root: ProductDrillRoot | null;
  clientId: string | null;
  customerName: string;
  headerControls: ReactNode;
  selectedPeriod: PeriodOption;
  selectedCustomDateRange?: DateRange;
}

interface Drill {
  root: ProductDrillRoot | null;
  /** The opened groups, outermost first; the last one is on screen. */
  trail: ProductGroupRef[];
  metric: CustomerMetric;
}

const startDrill = (root: ProductDrillRoot | null): Drill => ({
  root,
  trail: root ? [root.group] : [],
  metric: root?.initialMetric ?? "revenue",
});

/** One panel for the whole product tree: drilling replaces the level in place, however deep the tenant's tree goes. */
export const ProductDrilldownSidePanel = ({
  isOpen,
  onClose,
  isShrunk,
  root,
  clientId,
  customerName,
  headerControls,
  selectedPeriod,
  selectedCustomDateRange,
}: ProductDrilldownSidePanelProps) => {
  const t = useTranslations();
  const [state, setState] = useState(() => startDrill(root));
  // A new `root` restarts the drill without a remount, so the previous trail does not carry over.
  const drill = state.root === root ? state : startDrill(root);
  const depth = drill.trail.length;
  const current = drill.trail.at(-1);

  const goTo = (index: number) => setState({ ...drill, trail: drill.trail.slice(0, index + 1) });

  return (
    <ManagementSidepanel
      isOpen={isOpen}
      onClose={onClose}
      level={2}
      isLevel2Shrunk={isShrunk}
      back={depth > 1 ? { onBack: () => goTo(depth - 2), label: t("managementView.productGroup.back") } : undefined}
      leadingControls={depth > 2 ? <LevelSwitcher levels={drill.trail.slice(0, -1)} onSelect={goTo} /> : undefined}
      headerControls={headerControls}
    >
      {isOpen && current && clientId && (
        <ProductDrillLevel
          clientId={clientId}
          customerName={customerName}
          node={current}
          metric={drill.metric}
          onMetricChange={(metric) => setState({ ...drill, metric })}
          onOpen={(group) => setState({ ...drill, trail: [...drill.trail, group] })}
          selectedPeriod={selectedPeriod}
          selectedCustomDateRange={selectedCustomDateRange}
        />
      )}
    </ManagementSidepanel>
  );
};
